import React, { useMemo, useState } from "react";
import { Button } from "@material-ui/core";
import {
  AccessTimeOutlined,
  ChatBubbleOutline,
  CheckCircleOutline,
  ErrorOutline,
  GetApp,
  ScheduleOutlined,
  TrendingUp,
} from "@material-ui/icons";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import "./attendance-dashboard.css";

const numeric = value => {
  const result = Number(value);
  return Number.isFinite(result) ? Math.max(0, result) : 0;
};
const number = value => numeric(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
const duration = value => numeric(value) < 1 ? "<1 min" : `${number(value)} min`;
const shortDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || "") ? `${value.slice(8)}/${value.slice(5, 7)}` : String(value || "");
const weekdays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const bucketLabel = bucket => `${String(bucket * 2).padStart(2, "0")}h–${String(bucket * 2 + 2).padStart(2, "0")}h`;

function Metric({ label, value, note, icon: Icon, tone = "green" }) {
  return <article className={`attendance-metric attendance-tone-${tone}`}>
    <div className="attendance-metric-label"><span>{label}</span><span className="attendance-icon"><Icon /></span></div>
    <strong>{value}</strong>
    <p>{note}</p>
  </article>;
}

function Empty({ children }) { return <p className="attendance-empty">{children}</p>; }

function Heatmap({ points = [] }) {
  const cells = useMemo(() => {
    const values = Array.from({ length: 7 }, () => Array(12).fill(0));
    points.forEach(point => {
      const weekday = Number(point.weekday);
      const hour = Number(point.hour);
      if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7 || !Number.isFinite(hour) || hour < 0 || hour > 23) return;
      values[weekday - 1][Math.floor(hour / 2)] += numeric(point.total);
    });
    return values;
  }, [points]);
  const max = Math.max(1, ...cells.flat());

  return <div className="attendance-heatmap-scroll">
    <table className="attendance-heatmap" aria-label="Atendimentos iniciados por dia da semana e faixa de duas horas">
      <thead><tr><th className="attendance-heatmap-corner" scope="col">Dia</th>
        {Array.from({ length: 12 }, (_, bucket) => <th key={bucket} className="attendance-heatmap-hour" scope="col">{bucket % 2 === 0 ? `${String(bucket * 2).padStart(2, "0")}h` : ""}</th>)}
      </tr></thead>
      <tbody>{weekdays.map((day, dayIndex) => <tr key={day}>
        <th className="attendance-heatmap-day" scope="row">{day}</th>
        {cells[dayIndex].map((value, bucket) => {
          const ratio = value / max;
          const level = value === 0 ? 0 : ratio <= 0.25 ? 1 : ratio <= 0.5 ? 2 : ratio <= 0.75 ? 3 : 4;
          return <td key={`${day}-${bucket}`} className="attendance-heatmap-cell"><span role="img" className={`attendance-heat-cell attendance-level-${level}`}
            aria-label={`${day}, ${bucketLabel(bucket)}: ${number(value)} atendimentos iniciados`}
            title={`${day}, ${bucketLabel(bucket)}: ${number(value)} atendimentos iniciados`}
            >{value > 0 ? number(value) : ""}</span></td>;
        })}
      </tr>)}</tbody>
    </table>
  </div>;
}

function safeCell(value) {
  if (typeof value !== "string") return value;
  return /^[\s]*[=+@\-]/.test(value) ? `'${value}` : value;
}

export default function AttendanceDashboard({ data }) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  const daily = useMemo(() => (data?.daily || []).map(row => ({
    ...row,
    leads: numeric(row.leads),
    finished: numeric(row.finished),
  })), [data]);
  const heatmap = data?.heatmap || [];
  const totals = useMemo(() => daily.reduce((sum, row) => ({
    leads: sum.leads + row.leads,
    finished: sum.finished + row.finished,
  }), { leads: 0, finished: 0 }), [daily]);
  const peakDay = daily.reduce((top, row) => row.leads > (top?.leads || 0) ? row : top, null);
  const peakHour = heatmap.reduce((top, point) => numeric(point.total) > numeric(top?.total) ? point : top, null);
  const hasCounts = numeric(data?.total) > 0 || numeric(data?.finished) > 0 || numeric(data?.unread) > 0
    || numeric(data?.averagePerDay) > 0 || numeric(data?.averageWait) > 0 || numeric(data?.averageService) > 0
    || daily.some(row => row.leads > 0 || row.finished > 0) || heatmap.some(point => numeric(point.total) > 0);

  const exportSheet = async () => {
    setExporting(true);
    setExportError(false);
    try {
      const XLSX = await import("xlsx");
      const workbook = XLSX.utils.book_new();
      const summary = [
        { Indicador: "Total de atendimentos", Valor: numeric(data.total) },
        { Indicador: "Atendimentos finalizados", Valor: numeric(data.finished) },
        { Indicador: "Conversas não lidas", Valor: numeric(data.unread) },
        { Indicador: "Não lidas há mais de 24 horas", Valor: numeric(data.unreadOlderThan24h) },
        { Indicador: "Média de atendimentos por dia", Valor: numeric(data.averagePerDay) },
        { Indicador: "Tempo médio de espera (minutos)", Valor: numeric(data.averageWait) },
        { Indicador: "Tempo médio de atendimento (minutos)", Valor: numeric(data.averageService) },
      ].map(row => ({ Indicador: safeCell(row.Indicador), Valor: row.Valor }));
      const dailyRows = daily.map(row => ({ Data: safeCell(row.date || ""), Iniciados: row.leads, Finalizados: row.finished }));
      const heatRows = heatmap.map(point => ({
        Dia: safeCell(weekdays[Number(point.weekday) - 1] || "Dia não identificado"),
        Hora: numeric(point.hour),
        Atendimentos: numeric(point.total),
      }));
      [
        ["Resumo", summary],
        ["Por dia", dailyRows],
        ["Por dia e hora", heatRows],
      ].forEach(([name, rows]) => XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), name));
      XLSX.writeFile(workbook, "zappro-atendimento.xlsx");
    } catch (error) {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <div className="attendance-board"><Empty>Os dados de atendimento não estão disponíveis. Use Atualizar para tentar novamente.</Empty></div>;

  return <div className="attendance-board">
    <div className="attendance-intro">
      <p>Métricas de atendimento para o período selecionado</p>
      <Button startIcon={<GetApp />} onClick={exportSheet} disabled={exporting || !hasCounts} variant="outlined">
        {exporting ? "Preparando…" : "Planilha (.xlsx)"}
      </Button>
    </div>
    {exportError && <p className="attendance-export-error" role="alert">Não foi possível exportar. Tente novamente.</p>}

    {!hasCounts ? <Empty>Nenhum atendimento encontrado neste período.</Empty> : <>
      <div className="attendance-metrics">
        <Metric label="Total de atendimentos" value={number(data.total)} note="Criados no período selecionado" icon={ChatBubbleOutline} />
        <Metric label="Finalizados" value={number(data.finished)} note="Em status finalizado entre os criados no período" icon={CheckCircleOutline} tone="blue" />
        <Metric label="Média por dia" value={number(data.averagePerDay)} note="Atendimentos criados por dia" icon={TrendingUp} />
        <Metric label="Conversas não lidas" value={number(data.unread)} note={numeric(data.unreadOlderThan24h) ? `${number(data.unreadOlderThan24h)} sem atualização há mais de 24h` : "Entre os criados no período"} icon={ChatBubbleOutline} tone={numeric(data.unread) ? "red" : "green"} />
        <Metric label="Tempo médio de espera" value={duration(data.averageWait)} note="Em minutos" icon={ScheduleOutlined} tone="amber" />
        <Metric label="Tempo médio de atendimento" value={duration(data.averageService)} note="Duração média, em minutos" icon={AccessTimeOutlined} tone="blue" />
      </div>

      {numeric(data.unread) > 0 && <section className={`attendance-alert ${numeric(data.unreadOlderThan24h) > 0 ? "attendance-alert-red" : "attendance-alert-amber"}`} role="status">
        <ErrorOutline aria-hidden="true" />
        <div><h2>{number(data.unread)} conversas aguardam leitura</h2>
          <p>{numeric(data.unreadOlderThan24h) > 0
            ? `${number(data.unreadOlderThan24h)} têm mensagens não lidas e estão sem atualização há mais de 24 horas.`
            : "Há conversas com mensagens não lidas entre as criadas no período."}</p>
        </div>
      </section>}

      <div className="attendance-main-grid">
        <section className="attendance-panel">
          <header><h2>Entradas e finalizações por dia</h2><p>Atendimentos criados no período e quantos estão finalizados atualmente.</p></header>
          {daily.length ? <div className="attendance-chart"><ResponsiveContainer width="100%" height="100%"><ComposedChart data={daily} margin={{ top: 8, right: 10, bottom: 0, left: -18 }}>
            <CartesianGrid vertical={false} stroke="#e4eae7" strokeDasharray="3 4" />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: "#596b64" }} axisLine={false} tickLine={false} minTickGap={22} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#596b64" }} axisLine={false} tickLine={false} />
            <Tooltip labelFormatter={shortDate} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
            <Bar dataKey="leads" name="Iniciados" fill="#396bbc" radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false} />
            <Area dataKey="finished" name="Finalizados atualmente" type="monotone" stroke="#00855d" strokeWidth={2.5} fill="#00855d" fillOpacity={0.13} isAnimationActive={false} />
          </ComposedChart></ResponsiveContainer></div> : <Empty>Nenhum movimento diário para exibir neste período.</Empty>}
          <div className="attendance-chart-summary">
            <div><span>Atendimentos criados</span><strong>{number(totals.leads)}</strong></div>
            <div><span>Finalizados atualmente</span><strong>{number(totals.finished)}</strong></div>
            <div><span>Dia com mais inícios</span><strong>{peakDay ? `${shortDate(peakDay.date)} · ${number(peakDay.leads)}` : "Sem dados diários"}</strong></div>
          </div>
          <p className="attendance-footnote">Finalizados descreve o status atual dos atendimentos criados no período.</p>
        </section>

        <section className="attendance-panel attendance-heat-panel">
          <header><h2>Mapa de calor semanal</h2><p>Inícios de conversa por dia e faixa de duas horas.</p></header>
          {heatmap.length ? <Heatmap points={heatmap} /> : <Empty>Sem dados de horário para este período.</Empty>}
          {peakHour && numeric(peakHour.total) > 0 && <div className="attendance-peak"><span>Maior volume observado</span><strong>{weekdays[Number(peakHour.weekday) - 1] || "Dia"}, {String(Number(peakHour.hour) || 0).padStart(2, "0")}h · {number(peakHour.total)} inícios</strong></div>}
          <div className="attendance-legend"><span>Menor volume</span><i className="attendance-heat-swatch attendance-level-1" /><i className="attendance-heat-swatch attendance-level-2" /><i className="attendance-heat-swatch attendance-level-3" /><i className="attendance-heat-swatch attendance-level-4" /><span>Maior volume</span></div>
        </section>
      </div>

      {!daily.length && !heatmap.length && <p className="attendance-data-note">Os totais e tempos estão disponíveis; não há séries diárias ou horários para o período.</p>}
    </>}
  </div>;
}
